/**
 * One contact: who they are, and everything that has happened with them, newest first.
 *
 * TWO COLUMNS, THE CALL DIALOG'S. The history is the left pane - their calls and the team's
 * notes, interleaved by time, so "rang Monday, we noted he wants a text first, rang again
 * Thursday" reads as the sequence it was. The right is who they are, as plain rows.
 *
 * NOTHING THAT DOES NOT EARN ITS PLACE. Two buttons in the header (Call, Edit); Merge and
 * Delete live in Edit's footer, as on Categories. A detail nobody has filled in is not a row
 * with a dash in it, it is no row. Text is ink at reading size; grey is for dates and labels.
 *
 * A CALL IN THE HISTORY OPENS ON /calls, filtered to this contact (`?contact=`), so the call
 * dialog's previous and next step through this person's calls and nobody else's.
 */

import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, post, useApi, type CallSummary, type ContactDetail, type ContactNote, type ContactStatus, type ContactSummary } from "../api";
import { Dialog } from "../components/Dialog";
import { Empty, SkeletonRows } from "../components/States";
import { Calendar, ChevronDown, List, Mail, Pencil, Phone as PhoneIcon, Pin, Plus, Search, Tag } from "../components/Icons";
import { Field } from "../components/Form";
import { whyOpen } from "../components/Handled";
import { useCurrentAgent } from "../currentAgent";
import { useScopedTo } from "../components/Shell";
import { Button, ButtonLink, SettingCard, TextInput, cx } from "../ui";
import { dayKey, dayLabel, dayOf, duration, fullWhen, phone, timeOfDay, when } from "../format";
import { ContactForm, Pane, STATUS_WORDS, contactTitle, useContactList } from "./Contacts";

type Layer = "edit" | "follow-up" | "detail" | "merge" | "delete" | null;

export function ContactDialog() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { current } = useCurrentAgent();
  const { listHref, reload } = useContactList();
  const { data, error, loading } = useApi<{ contact: ContactDetail }>(id ? `/v1/contacts/${id}` : null);
  // The contact as the last write returned it, laid over the fetched one - the call dialog's
  // `saved`, for the same reason: every write answers with the whole contact.
  const [saved, setSaved] = useState<ContactDetail | null>(null);
  const contact = saved && saved.id === id ? saved : (data?.contact ?? null);
  const [layer, setLayer] = useState<Layer>(null);
  /** The detail being changed. */
  const [detail, setDetail] = useState<string | null>(null);
  const tz = current?.timezone;

  const close = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate(listHref);
  };

  const changed = (next: ContactDetail) => {
    setSaved(next);
    reload();
  };

  return (
    <Dialog
      size="wide"
      bare
      // Escape backs out of a form in the side panel first, and closes the contact after.
      onClose={() => {
        if (layer) setLayer(null);
        else close();
      }}
      title={contact ? contactTitle(contact) : "Contact"}
      meta={contact?.company ?? undefined}
      aside={
        contact && (
          <div className="flex items-center gap-2">
            {/* THE OWNER RINGS THEM. The agent does not: it cannot place a call from here, and
                a contact is the owner's filing, never the agent's script. */}
            {contact.phones[0] && (
              <ButtonLink variant="primary" href={`tel:${contact.phones[0]}`}>
                <PhoneIcon size={15} /> Call
              </ButtonLink>
            )}
            {!layer && <Button onClick={() => setLayer("edit")}>Edit</Button>}
          </div>
        )
      }
    >
      {error ? (
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          <Empty
            compact
            icon={error.status === 404 ? Search : undefined}
            tone={error.status === 404 ? undefined : "alert"}
            title={error.status === 404 ? "No such contact" : "Couldn't load this contact"}
            body={error.status === 404 ? "It may have been merged into another, or deleted." : error.message}
            action={{ label: "Back to contacts", onClick: close }}
          />
        </div>
      ) : (
        <div
          className={cx(
            "grid min-h-0 flex-1 grid-cols-[minmax(0,3fr)_minmax(0,2fr)]",
            "max-[900px]:grid-cols-[minmax(0,1fr)] max-[900px]:content-start max-[900px]:overflow-y-auto",
          )}
        >
          <div className="min-h-0 overflow-y-auto px-6 py-5 max-[900px]:min-h-auto max-[900px]:overflow-visible">
            {loading || !contact ? <SkeletonRows rows={6} /> : <History contact={contact} tz={tz} onChanged={changed} />}
          </div>

          <aside
            className={cx(
              "min-h-0 overflow-y-auto border-l border-line px-6 py-5",
              "max-[900px]:min-h-auto max-[900px]:overflow-visible max-[900px]:border-t max-[900px]:border-l-0",
            )}
          >
            {contact && !layer && (
              <SettingCard flush>
                <About
                  contact={contact}
                  onChanged={changed}
                  onFollowUp={() => setLayer("follow-up")}
                  onDetail={(label) => {
                    setDetail(label);
                    setLayer("detail");
                  }}
                />
              </SettingCard>
            )}
            {/* EVERYTHING YOU DO TO A CONTACT HAPPENS HERE, in place of its details - never
                in a second dialog over this one. */}
            {contact && layer === "edit" && (
              <ContactForm
                title="Edit contact"
                inline
                path={`/v1/contacts/${contact.id}`}
                initial={contact}
                onClose={() => setLayer(null)}
                onSaved={(next) => {
                  setLayer(null);
                  changed(next);
                }}
                onMerge={() => setLayer("merge")}
                onDelete={() => setLayer("delete")}
              />
            )}
            {contact && layer === "follow-up" && (
              <FollowUpPane
                contact={contact}
                onClose={() => setLayer(null)}
                onSaved={(next) => {
                  setLayer(null);
                  changed(next);
                }}
              />
            )}
            {contact && layer === "detail" && detail && (
              <DetailPane
                contact={contact}
                label={detail}
                onClose={() => setLayer(null)}
                onSaved={(next) => {
                  setLayer(null);
                  changed(next);
                }}
              />
            )}
            {contact && layer === "merge" && (
              <MergePane
                contact={contact}
                onClose={() => setLayer(null)}
                onMerged={(into) => {
                  setLayer(null);
                  reload();
                  // This contact is gone; the one it went into is where its history now is.
                  navigate(listHref.replace(/^\/contacts/, `/contacts/${into.id}`), { replace: true });
                }}
              />
            )}
            {contact && layer === "delete" && (
              <DeletePane
                contact={contact}
                onClose={() => setLayer(null)}
                onDeleted={() => {
                  setLayer(null);
                  reload();
                  navigate(listHref, { replace: true });
                }}
              />
            )}
          </aside>
        </div>
      )}

    </Dialog>
  );
}

/**
 * The calls of theirs still waiting on a person, each saying what it was, why it needs
 * somebody (`whyOpen`, the calls page's own words) and when - so "Mark handled" is pressed
 * knowing what it closes. Absent when nothing is waiting.
 */
function Waiting({
  contact,
  tz,
  onChanged,
}: {
  contact: ContactDetail;
  tz: string | null | undefined;
  onChanged: (next: ContactDetail) => void;
}) {
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const to = useScopedTo();
  const open = contact.history.filter((call) => call.needsPerson && !call.handled);
  if (!open.length) return null;

  async function handle(call: CallSummary) {
    setBusy(call.id);
    setError(null);
    try {
      await post(`/v1/calls/${call.id}/handled`, { handled: true });
      onChanged((await api<{ contact: ContactDetail }>(`/v1/contacts/${contact.id}`)).contact);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="grid gap-2">
      <h3 className="text-sm font-semibold text-ink">Waiting on you</h3>
      <SettingCard flush>
        <ul className="[&>li+li]:border-t [&>li+li]:border-line-soft">
          {open.map((call) => (
            <li key={call.id} className="flex items-center gap-3 px-4 py-3">
              <Link
                to={to(`/calls/${call.id}?contact=${encodeURIComponent(contact.id)}`)}
                className="grid min-w-0 flex-1 gap-0.5 hover:underline"
              >
                <span className="text-sm font-medium text-ink">{titleOf(call)}</span>
                <span className="text-sm text-ink">
                  {whyOpen(call)} {when(call.startedAt, tz)}.
                </span>
              </Link>
              <Button disabled={busy === call.id} onClick={() => void handle(call)}>
                Mark handled
              </Button>
            </li>
          ))}
        </ul>
      </SettingCard>
      {error && <p className="text-sm text-alert">{error}</p>}
    </section>
  );
}

/**
 * Who they are, as the rows of a settings summary card: an icon and a label on the left, the
 * value on the right. Only what is known gets a row - except status and follow-up, which are
 * the two things somebody comes here to change.
 */
function About({
  contact,
  onChanged,
  onFollowUp,
  onDetail,
}: {
  contact: ContactDetail;
  onChanged: (next: ContactDetail) => void;
  onFollowUp: () => void;
  onDetail: (label: string) => void;
}) {
  return (
    <dl className="text-sm text-ink [&>div+div]:border-t [&>div+div]:border-line-soft">
      <Row icon={<Tag size={16} />} label="Status">
        <StatusPicker contact={contact} onChanged={onChanged} />
      </Row>
      <Row icon={<Calendar size={16} />} label="Follow up">
        <FollowUp contact={contact} onEdit={onFollowUp} />
      </Row>
      {contact.phones.length > 0 && (
        <Row icon={<PhoneIcon size={16} />} label="Phone">
          {contact.phones.map((number) => (
            <a key={number} href={`tel:${number}`} className="nums block hover:underline">
              {phone(number)}
            </a>
          ))}
        </Row>
      )}
      {contact.emails.length > 0 && (
        <Row icon={<Mail size={16} />} label="Email">
          {contact.emails.map((email) => (
            <a key={email} href={`mailto:${email}`} className="block break-all hover:underline">
              {email}
            </a>
          ))}
        </Row>
      )}
      {contact.location && (
        <Row icon={<Pin size={16} />} label="Address">
          {contact.location}
        </Row>
      )}
      {/* KEPT BY THE AGENT'S ACTIONS, or by a person. Click to change or remove one. */}
      {contact.fields.map((field) => (
        <Row key={field.label} icon={<List size={16} />} label={field.label}>
          <button type="button" onClick={() => onDetail(field.label)} className="cursor-pointer text-right hover:underline">
            {field.value}
          </button>
        </Row>
      ))}
    </dl>
  );
}

function Row({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <div className="flex min-h-12 items-center gap-3 px-4 py-2.5">
      <span className="flex-none text-ink">{icon}</span>
      <dt className="flex-none">{label}</dt>
      <dd className="ml-auto min-w-0 text-right break-words">{children}</dd>
    </div>
  );
}

/**
 * Status, changed in place, looking like the value it is until hovered. "Automatic" is the
 * calls' reading and names what it currently is; only a person ever says Customer.
 */
function StatusPicker({ contact, onChanged }: { contact: ContactDetail; onChanged: (next: ContactDetail) => void }) {
  const [error, setError] = useState<string | null>(null);
  const automatic = STATUS_WORDS[contact.status.fromCalls ?? "new"];
  return (
    <>
      <span className="relative -mr-1.5 inline-flex items-center">
        <select
          aria-label="Status"
          className="cursor-pointer appearance-none rounded-sm bg-transparent py-1 pr-6 pl-2 text-right font-medium text-ink hover:bg-sunken focus-visible:bg-sunken focus-visible:outline-none"
          value={contact.status.set ?? "auto"}
          onChange={async (event) => {
            setError(null);
            const status = event.target.value === "auto" ? null : (event.target.value as ContactStatus);
            try {
              onChanged((await post<{ contact: ContactDetail }>(`/v1/contacts/${contact.id}/status`, { status })).contact);
            } catch (err) {
              setError((err as Error).message);
            }
          }}
        >
          <option value="auto">{automatic} (automatic)</option>
          <option value="lead">{STATUS_WORDS.lead}</option>
          <option value="customer">{STATUS_WORDS.customer}</option>
          <option value="not_a_fit">{STATUS_WORDS.not_a_fit}</option>
        </select>
        <ChevronDown size={14} className="pointer-events-none absolute right-1.5 text-ink" />
      </span>
      {error && <p className="text-alert">{error}</p>}
    </>
  );
}

/** The date to get back to them, or a way to set one. Clearing it is in its dialog. */
function FollowUp({ contact, onEdit }: { contact: ContactDetail; onEdit: () => void }) {
  const followUp = contact.followUp;
  return (
    <button type="button" onClick={onEdit} className="-mr-2 cursor-pointer rounded-sm px-2 py-1 text-right hover:bg-sunken">
      {followUp ? (
        <>
          <span className={cx("block", followUp.due && "font-medium")}>
            {dayOf(followUp.on)}
            {followUp.due && ", due"}
          </span>
          {followUp.note && <span className="block">{followUp.note}</span>}
        </>
      ) : (
        <span className="inline-flex items-center gap-1 font-medium">
          <Plus size={14} /> Set a date
        </span>
      )}
    </button>
  );
}

type Entry = { kind: "call"; at: number; call: CallSummary } | { kind: "note"; at: number; note: ContactNote };

/**
 * Calls and notes as one timeline, newest first, under a heading per day - so each entry
 * carries only its time, and "rang Monday, we noted he wants a text first, rang again
 * Thursday" reads as the sequence it was. The note box sits above it.
 */
function History({
  contact,
  tz,
  onChanged,
}: {
  contact: ContactDetail;
  tz: string | null | undefined;
  onChanged: (next: ContactDetail) => void;
}) {
  const to = useScopedTo();
  const entries: Entry[] = [
    ...contact.history.map((call): Entry => ({ kind: "call", at: call.startedAt, call })),
    ...contact.notes.map((note): Entry => ({ kind: "note", at: note.at, note })),
  ].sort((a, b) => b.at - a.at);
  const days: Array<{ key: string; label: string; entries: Entry[] }> = [];
  for (const entry of entries) {
    const key = dayKey(entry.at, tz);
    const last = days.at(-1);
    if (last?.key === key) last.entries.push(entry);
    else days.push({ key, label: dayLabel(entry.at, tz), entries: [entry] });
  }

  return (
    <div className="grid gap-6">
      <Waiting contact={contact} tz={tz} onChanged={onChanged} />
      <NoteBox contact={contact} onChanged={onChanged} />
      {days.map((day) => (
        <section key={day.key} className="grid gap-1">
          <h3 className="text-sm font-semibold text-ink">{day.label}</h3>
          <ol>
            {day.entries.map((entry, index) => (
              <li key={entry.kind === "call" ? `c${entry.call.id}` : entry.note.id} className="relative">
                {/* THE RAIL: a line from this entry's icon down to the next one's, so a day
                    reads as one sequence rather than a stack of rows. */}
                {index < day.entries.length - 1 && (
                  <span aria-hidden="true" className="absolute top-11 -bottom-3 left-[15px] w-px bg-line" />
                )}
                {entry.kind === "call" ? (
                  <CallEntry call={entry.call} contact={contact} tz={tz} />
                ) : (
                  <NoteEntry note={entry.note} contact={contact} tz={tz} onChanged={onChanged} />
                )}
              </li>
            ))}
          </ol>
        </section>
      ))}
      {contact.moreCalls && (
        <Link
          className="text-sm font-medium text-ink hover:underline"
          to={to(`/calls?contact=${encodeURIComponent(contact.id)}`)}
        >
          All of their calls
        </Link>
      )}
    </div>
  );
}

/** One entry: its icon on the rail, what happened, and its time on the right. */
function Entry({ icon, time, children }: { icon: ReactNode; time: ReactNode; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[32px_minmax(0,1fr)_auto] items-start gap-3 py-3">
      <span className="relative grid size-8 place-items-center rounded-full border border-line bg-paper text-ink">{icon}</span>
      <div className="grid min-w-0 gap-1 pt-1.5">{children}</div>
      <span className="nums pt-1.5 text-sm whitespace-nowrap text-ink">{time}</span>
    </div>
  );
}

/** What a call was about: the agent's headline, else what the reading says they wanted. */
function subjectOf(call: CallSummary): string | null {
  return call.outcome.headline ?? call.analysis.ask ?? null;
}

/** A call's title: its subject, else which way it went. */
function titleOf(call: CallSummary): string {
  return subjectOf(call) ?? direction(call);
}

function direction(call: CallSummary): string {
  return call.direction === "outbound" ? "Outgoing call" : "Incoming call";
}

function CallEntry({ call, contact, tz }: { call: CallSummary; contact: ContactSummary; tz: string | null | undefined }) {
  const to = useScopedTo();
  return (
    <Link
      to={to(`/calls/${call.id}?contact=${encodeURIComponent(contact.id)}`)}
      className="group block"
    >
      <Entry icon={<PhoneIcon size={15} />} time={<span title={fullWhen(call.startedAt, tz)}>{timeOfDay(call.startedAt, tz)}</span>}>
        <span className="text-sm font-medium text-ink group-hover:underline">{titleOf(call)}</span>
        {/* Which way and how long - the direction only when the title has not already said it. */}
        <span className="text-sm text-ink">
          {subjectOf(call) ? `${direction(call)} · ${duration(call.durationMs)}` : duration(call.durationMs)}
        </span>
        {call.analysis.summary && <span className="line-clamp-2 text-sm text-ink">{call.analysis.summary}</span>}
      </Entry>
    </Link>
  );
}

/** A note from the team, set apart from the calls: a tinted box with who wrote it. */
function NoteEntry({
  note,
  contact,
  tz,
  onChanged,
}: {
  note: ContactNote;
  contact: ContactSummary;
  tz: string | null | undefined;
  onChanged: (next: ContactDetail) => void;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <div className="group">
      <Entry icon={<Pencil size={14} />} time={timeOfDay(note.at, tz)}>
        <div className="grid gap-1 rounded-lg bg-sunken px-3.5 py-2.5">
          <p className="text-sm whitespace-pre-wrap text-ink">{note.body}</p>
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-ink">Note by {note.mine ? "you" : (note.by ?? "your team")}</span>
            {/* On hover only: deleting a note is rare, and a bin on every note is clutter. */}
            <button
              type="button"
              aria-label="Delete this note"
              disabled={busy}
              className="cursor-pointer text-sm text-ink underline opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
              onClick={async () => {
                setBusy(true);
                try {
                  onChanged(
                    (await post<{ contact: ContactDetail }>(`/v1/contacts/${contact.id}/notes/${note.id}/delete`, {}))
                      .contact,
                  );
                } finally {
                  setBusy(false);
                }
              }}
            >
              Delete
            </button>
          </div>
        </div>
      </Entry>
    </div>
  );
}

/** One line to write a note; Enter adds it. The button appears once there is something to add. */
function NoteBox({ contact, onChanged }: { contact: ContactSummary; onChanged: (next: ContactDetail) => void }) {
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function add(event: React.FormEvent) {
    event.preventDefault();
    if (!body.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const { contact: next } = await post<{ contact: ContactDetail }>(`/v1/contacts/${contact.id}/notes`, { body });
      setBody("");
      onChanged(next);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={add} className="grid gap-1.5">
      <div className="flex items-center gap-2">
        <TextInput
          aria-label="Add a note"
          placeholder="Add a note"
          className="min-w-0 flex-1"
          maxLength={2000}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        {body.trim() && (
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? "Adding…" : "Add note"}
          </Button>
        )}
      </div>
      {error && <p className="text-sm text-alert">{error}</p>}
    </form>
  );
}

function FollowUpPane({
  contact,
  onClose,
  onSaved,
}: {
  contact: ContactDetail;
  onClose: () => void;
  onSaved: (next: ContactDetail) => void;
}) {
  const [on, setOn] = useState(contact.followUp?.on ?? "");
  const clear = async () => {
    setError(null);
    try {
      onSaved((await post<{ contact: ContactDetail }>(`/v1/contacts/${contact.id}/follow-up`, { clear: true })).contact);
    } catch (err) {
      setError((err as Error).message);
    }
  };
  const [note, setNote] = useState(contact.followUp?.note ?? "");
  const [error, setError] = useState<string | null>(null);
  const form = "follow-up-form";

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      onSaved((await post<{ contact: ContactDetail }>(`/v1/contacts/${contact.id}/follow-up`, { on, note })).contact);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <Pane
      title={`Follow up with ${contactTitle(contact)}`}
      onClose={onClose}
      footer={
        <>
          {contact.followUp && (
            <Button type="button" variant="danger" onClick={() => void clear()}>
              Remove
            </Button>
          )}
          <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
            {error && <span className="text-sm text-alert">{error}</span>}
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" form={form} variant="primary" disabled={!on}>
              Save
            </Button>
          </div>
        </>
      }
    >
      <form id={form} onSubmit={submit} className="grid gap-4">
        <Field label="When">
          <TextInput type="date" className="w-full" autoFocus value={on} onChange={(e) => setOn(e.target.value)} />
        </Field>
        <Field label="What about">
          <TextInput className="w-full" maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </form>
    </Pane>
  );
}


/** One detail kept on the contact, changed or removed by a person. Empty removes it. */
function DetailPane({
  contact,
  label: existing,
  onClose,
  onSaved,
}: {
  contact: ContactDetail;
  label: string;
  onClose: () => void;
  onSaved: (next: ContactDetail) => void;
}) {
  const [value, setValue] = useState(contact.fields.find((field) => field.label === existing)?.value ?? "");
  const [error, setError] = useState<string | null>(null);
  const form = "detail-form";

  async function send(next: string | null) {
    setError(null);
    try {
      onSaved(
        (await post<{ contact: ContactDetail }>(`/v1/contacts/${contact.id}/fields`, { label: existing, value: next }))
          .contact,
      );
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <Pane
      title={existing}
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="danger" onClick={() => void send(null)}>
            Remove
          </Button>
          <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
            {error && <span className="text-xs text-alert">{error}</span>}
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" form={form} variant="primary">
              Save
            </Button>
          </div>
        </>
      }
    >
      <form
        id={form}
        className="grid gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void send(value);
        }}
      >
        <Field label="Value">
          <TextInput className="w-full" autoFocus maxLength={500} value={value} onChange={(e) => setValue(e.target.value)} />
        </Field>
      </form>
    </Pane>
  );
}


/**
 * Fold this contact into another. The one picked is the one that stays, with its own name;
 * everything of this one's moves onto it.
 */
function MergePane({
  contact,
  onClose,
  onMerged,
}: {
  contact: ContactDetail;
  onClose: () => void;
  onMerged: (into: ContactDetail) => void;
}) {
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);
  const { data } = useApi<{ contacts: ContactSummary[] }>(
    `/v1/contacts?agent=${encodeURIComponent(contact.agentId)}${debounced ? `&q=${encodeURIComponent(debounced)}` : ""}`,
  );
  const others = (data?.contacts ?? []).filter((other) => other.id !== contact.id);
  const [picked, setPicked] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const target = others.find((other) => other.id === picked);

  async function merge() {
    if (!picked) return;
    setSaving(true);
    setError(null);
    try {
      const { contact: into } = await post<{ contact: ContactDetail }>(`/v1/contacts/${contact.id}/merge`, { into: picked });
      onMerged(into);
    } catch (err) {
      setError((err as Error).message);
      setSaving(false);
    }
  }

  return (
    <Pane
      title={`Merge ${contactTitle(contact)} into…`}
      onClose={onClose}
      footer={
        <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
          {error && <span className="text-xs text-alert">{error}</span>}
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!picked || saving} onClick={() => void merge()}>
            {saving ? "Merging…" : "Merge"}
          </Button>
        </div>
      }
    >
      <div className="grid gap-3">
        <p className="text-sm text-ink">
          {target
            ? `${contactTitle(contact)}'s calls, numbers, emails and notes move to ${contactTitle(target)}, and ${contactTitle(contact)} is removed.`
            : "Pick who this really is. Their calls, numbers, emails and notes move there."}
        </p>
        <TextInput
          type="search"
          aria-label="Find a contact"
          placeholder="Name or number"
          className="w-full"
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div role="radiogroup" aria-label="Contacts" className="grid max-h-72 overflow-y-auto rounded-sm border border-line">
          {others.length === 0 && <p className="px-3 py-2.5 text-sm text-ink">Nobody else matches.</p>}
          {others.map((other, index) => (
            <button
              key={other.id}
              type="button"
              role="radio"
              aria-checked={picked === other.id}
              onClick={() => setPicked(other.id)}
              className={cx(
                "grid cursor-pointer px-3 py-2 text-left",
                index > 0 && "border-t border-line-soft",
                picked === other.id ? "bg-ink text-paper" : "hover:bg-sunken",
              )}
            >
              <span className="text-sm font-medium">{contactTitle(other)}</span>
              <span className={cx("text-sm", picked === other.id ? "text-paper" : "text-ink")}>
                {[other.name ? phone(other.phones[0]) : null, other.company, other.calls === 1 ? "1 call" : `${other.calls} calls`]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </button>
          ))}
        </div>
      </div>
    </Pane>
  );
}

function DeletePane({
  contact,
  onClose,
  onDeleted,
}: {
  contact: ContactDetail;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  return (
    <Pane
      title={`Delete ${contactTitle(contact)}?`}
      onClose={onClose}
      footer={
        <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
          {error && <span className="text-xs text-alert">{error}</span>}
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={saving}
            onClick={async () => {
              setSaving(true);
              try {
                await post(`/v1/contacts/${contact.id}/delete`, {});
                onDeleted();
              } catch (err) {
                setError((err as Error).message);
                setSaving(false);
              }
            }}
          >
            Delete
          </Button>
        </div>
      }
    >
      <p className="text-sm text-ink">
        Their notes go with them. Their calls stay on the Calls page. If they ring again, they start a new contact.
      </p>
    </Pane>
  );
}
